import React from "react";
import { Box, Dialog, DialogContent, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";

import StyledButton from "default/components/StyledButton";

const HIGURASHI_CHAPTER_TITLES = {
  onikakushi: "鬼隠し編",
};

const OtherCharacterItem = ({ soul }) => {
  const isClickable = Boolean(soul.lineUrl);

  return (
    <Box
      component={isClickable ? "a" : "div"}
      href={isClickable ? soul.lineUrl : undefined}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        width: "100%",
        minHeight: 118,
        px: 2,
        py: 2,
        borderRadius: 2,
        boxSizing: "border-box",
        color: theme.system.colors.text.primary,
        textDecoration: "none",
        backgroundColor: "rgba(0, 0, 0, 0.58)",
        border: "1px solid rgba(191, 33, 32, 0.5)",
        boxShadow: "0 0 16px rgba(191, 33, 32, 0.46)",
        cursor: isClickable ? "pointer" : "default",
        transition:
          "opacity 160ms ease, box-shadow 160ms ease, transform 160ms ease",
        "&:visited": {
          color: theme.system.colors.text.primary,
        },
        "&:focus": {
          color: theme.system.colors.text.primary,
          textDecoration: "none",
        },
        "&:hover": isClickable
          ? {
              opacity: 0.92,
              color: theme.system.colors.text.primary,
              textDecoration: "none",
              boxShadow: "0 0 24px rgba(191, 33, 32, 0.68)",
              transform: "translateY(-1px)",
            }
          : undefined,
        [theme.breakpoints.down("sm")]: {
          px: 1.5,
          minHeight: 108,
        },
      })}
    >
      <Box
        sx={{
          width: 88,
          height: 88,
          borderRadius: "50%",
          overflow: "hidden",
          mr: 2,
          flexShrink: 0,
          boxShadow: "0 0 16px #BF2120",
        }}
      >
        <img
          src={soul.imageURL}
          alt={soul.name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </Box>

      <Box
        sx={{
          minWidth: 0,
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={(theme) => ({
            fontFamily: theme.system.fonts.body,
            fontSize: "22px",
            fontWeight: 800,
            color: theme.system.colors.text.primary,
            lineHeight: 1.35,
            textAlign: "center",
          })}
        >
          {soul.name}
        </Typography>
      </Box>
      <ChevronRightIcon
        aria-hidden="true"
        sx={{
          ml: 1.5,
          flexShrink: 0,
          color: "rgba(255, 255, 255, 0.86)",
          filter: "drop-shadow(0 0 6px rgba(191, 33, 32, 0.8))",
        }}
      />
    </Box>
  );
};

const OtherCharactersDialogContent = ({ chapterTitle, souls, t }) => (
  <Box
    sx={{
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: 2,
    }}
  >
    <Typography
      id="other-characters-dialog-title"
      sx={(theme) => ({
        fontFamily: theme.system.fonts.body,
        fontSize: "24px",
        fontWeight: 900,
        color: theme.system.colors.text.primary,
        textAlign: "center",
        lineHeight: 1.35,
        mb: 1,
        textShadow: "0 0 10px rgba(191, 33, 32, 0.9)",
      })}
    >
      {chapterTitle}
    </Typography>

    {souls.length > 0 ? (
      souls.map((soul) => (
        <OtherCharacterItem key={soul.character} soul={soul} />
      ))
    ) : (
      <Typography
        sx={(theme) => ({
          fontFamily: theme.system.fonts.body,
          fontSize: "14px",
          color: theme.system.colors.text.secondary,
          textAlign: "center",
        })}
      >
        {t("higurashi:button.no_other_characters")}
      </Typography>
    )}
  </Box>
);

const OtherCharactersButton = ({ chapter, souls }) => {
  const { t } = useTranslation(["higurashi"]);
  const [open, setOpen] = React.useState(false);
  const chapterTitle = HIGURASHI_CHAPTER_TITLES[chapter] || chapter;

  return (
    <>
      <StyledButton variant="contained" onClick={() => setOpen(true)}>
        {t("higurashi:button.other_characters")}
      </StyledButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="other-characters-dialog-title"
        PaperProps={{
          sx: {
            width: "min(92vw, 420px)",
            m: 0,
            p: 0,
            backgroundColor: "transparent",
            boxShadow: "none",
            borderRadius: 0,
            overflow: "visible",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.84)",
          },
        }}
      >
        <DialogContent
          sx={{
            p: 2,
            borderRadius: 2,
            background: "rgba(0, 0, 0, 0.78)",
            border: "1px solid rgba(191, 33, 32, 0.48)",
            boxShadow: "0 0 28px rgba(191, 33, 32, 0.5)",
          }}
        >
          <OtherCharactersDialogContent
            chapterTitle={chapterTitle}
            souls={souls}
            t={t}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OtherCharactersButton;
